import React, { useState, useContext, useEffect } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { SharedStateContext } from '../../App';
import { useStores } from '@a2d24/care-at-work-state/stores';
import { useConsultSummary, useMedicalHistory } from '../../queries';
import CompleteConsultAction from './SignOffActions/CompleteConsultAction';
import ProceedToSignoffAction from './SignOffActions/ProceedToSignoffAction';
import RequestSignoffAction from './SignOffActions/RequestSignoffAction';
import ProceedToDiseaseMng from './SignOffActions/ProceedToDiseaseMng';
import SignoffSummaryDialog from './SignoffSummaryDialog';
import { approveSummary, getConsultSummaryForConsult } from '../../Apis/EpisodeOfCare';
import { useSnackbar } from 'notistack';
import { consultSummaryTypes } from '../const';

const SignoffBanner = ({ config, props }) => {
    const classes = useStyles();
    const sharedState = useContext(SharedStateContext);
    const [submitting, setSubmitting] = useState(false);
    const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
    const { authStore } = useStores();
    const is_stock_module_site = authStore.dsp_details?.is_stock_module_site;
    const patient_id = `${props.consult.patient.primary_identifier.value}`;
    const { isLoading, isError, data, error } = useMedicalHistory(patient_id);
    const medicalHistoryData = data;
    const consultCofStatus = props.consult?.rules_result?.cof_result?.status?.name;
    const { enqueueSnackbar } = useSnackbar();
    const consultSummaryQuery = useConsultSummary(props?.consult);
    useEffect(() => {
        if(consultSummaryTypes.includes(props.consult.consult_type)) {
            if(!consultSummaryQuery.data?.approved){
                setSummaryDialogOpen(true);
            }
            if(consultSummaryQuery.isFetched && props.consult.status === 'completed' && !(consultSummaryQuery.data?.summary_id)){
                enqueueSnackbar("Unable to generate summary", { variant: 'error' })
            }
        }
    }, []);
    const renderAction = () => {
        switch (props.consult.consult_status) {
            case 'procedures_completed':
                return (
                    <div style={{ display: 'inline-flex' }}>
                        <div style={{ marginLeft: '5px', marginRight: '5px' }}>
                            <RequestSignoffAction
                                consult={props.consult}
                                setSubmitting={setSubmitting}
                                submitting={submitting}
                                role={authStore.selectedRole.role}
                            />
                        </div>
                        <div style={{ marginLeft: '5px', marginRight: '5px' }}>
                            {['Fit for duty', 'Fit for duty with follow-up', 'Fit for duty with conditions'].includes(consultCofStatus) ? (
                                <div style={{ marginLeft: '5px', marginRight: '5px' }}>
                                    <ProceedToSignoffAction
                                        consult={props.consult}
                                        submitting={submitting}
                                        is_quick_signoff={true}
                                        text={'finalise examination'}
                                    />
                                </div>
                            ) : null}
                        </div>
                        <div style={{ marginLeft: '5px', marginRight: '5px' }}>
                            {'signoff_required' in props.consult &&
                            !props.consult.signoff_required ? (
                                <>
                                    <CompleteConsultAction
                                        consult={props.consult}
                                        setSubmitting={setSubmitting}
                                        submitting={submitting}
                                    />
                                </>
                            ) : null}
                        </div>
                    </div>
                );

            case 'awaiting_omp':
                if (['dsp_omp', 'global_omp'].includes(authStore.selectedRole.role)){
                    return (
                        <ProceedToSignoffAction consult={props.consult} submitting={submitting} />
                    );
                } else {
                    return null;
                }
            case 'completed':
                return <ProceedToDiseaseMng consult={props.consult} disabled={submitting||(consultSummaryQuery?.data && !consultSummaryQuery?.data?.approved)} />;
            default:
                return null;
        }
    };

    const medicationListValid = () => {
        const medications = sharedState.medications || [];
        const checkMedicationIsInvalid = (item) => {
            return item.is_batch_item === true && item.batch_stock_moves_captured !== true;
        };

        return !medications.some(checkMedicationIsInvalid);
    };

    const renderBanner = () => {
        if (!medicalHistoryData) {
            return (
                <div className={classes.containerYellow}>
                    <div className={classes.text}>
                        Please capture Medical History, including vaccination history, for patient
                        in Medical and Contextual History widget
                    </div>
                </div>
            );
        }
        if (is_stock_module_site) {
            if (!medicationListValid()) {
                return (
                    <div className={classes.containerYellow}>
                        <div className={classes.text}>
                            Please Capture the Batch Details for Medication and Consumables
                        </div>
                    </div>
                );
            } else {
                return renderConsultBasedBanner();
            }
        } else {
            return renderConsultBasedBanner();
        }
    };

    const renderConsultBasedBanner = () => {
        return (
            <>
                {['procedures_completed', 'awaiting_omp'].indexOf(props.consult.consult_status) >
                -1 ? (
                    <div className={classes.container}>
                        <div className={classes.text}>All Medical Analysis Completed</div>
                        {renderAction()}
                    </div>
                ) : null}
                {['completed'].indexOf(props.consult.consult_status) > -1 ? (
                    <div className={classes.container}>
                        <div className={classes.text}>Consult Finalised</div>
                        {renderAction()}
                    </div>
                ) : null}
                {
                    props.consult.consult_status === 'completed' && consultSummaryTypes.includes(props.consult.consult_type) && !(consultSummaryQuery?.data?.approved)?
                        <SignoffSummaryDialog
                            open={summaryDialogOpen}
                            onSubmit={(summary)=>{
                                approveSummary(props.consult.consult_id, summary).then(()=>{
                                    consultSummaryQuery.refetch();
                                    enqueueSnackbar("Summary approved", { variant: 'success' });
                                }).finally(()=>{
                                    setSummaryDialogOpen(false);
                                })

                            }}
                            summary={consultSummaryQuery?.data?.original_summary}
                            onClose={()=>{}}
                        />
                        :null
                }
            </>
        );
    };
    return <React.Fragment>{renderBanner()}</React.Fragment>;
};

const useStyles = makeStyles((theme) => ({
    container: {
        backgroundColor: theme.palette.success.main,
        borderRadius: '3px',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(3),
    },
    containerYellow: {
        backgroundColor: theme.palette.warning.main,
        borderRadius: '3px',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(3),
    },
    text: {
        fontSize: '14px',
        fontWeight: 'bold',
    },
}));

export default SignoffBanner;
